import React, { useCallback, useMemo, useState } from "react"
import { useSnapshot } from "valtio"
import { DATA_CLICK_AWAY_ID, DomNodes } from "../../constants"
import {
  getChildrenWithInjectedProps,
  transformChoicesConstraints,
  useAssessmentItemResponseScoring,
  useFindNodes,
  useOutsideClick,
  useSelectableChoice,
  percentageFormatter,
  shuffleArray,
  useKeyboardNavigation
} from "../../helpers"
import { CurrentTestState, ScoringState } from "../../storage"
import { ConstraintsAlertContainer } from "../../ui/containers/ConstraintsAlertContainer"
import {
  Cardinality,
  InteractionType,
  onSaveStudentAnswer,
  CheckCorrect,
  isQuestionComplete,
  ContainerType,
  NeutralSetting
} from "../../types"
import { Box, Stack, styled, Typography } from "@mui/material"
import { SimpleChoiceComponent } from "../SimpleChoices"
import { pluralizeList } from "../../helpers/converters/pluralizeList"

interface ChoiceInteractionProps {
  itemId: string
  componentNode: DomNodes
  responseidentifier?: string
  shuffle?: string
  maxchoices?: string
  minchoices?: string
  startLetter?: string
  orientation?: string
  children?: JSX.Element[]
  accessibilityAttr?: { [key: string]: string }
  showAnswer?: boolean
  onSaveStudentAnswer?: onSaveStudentAnswer
  checkCorrect?: CheckCorrect
  isCompletedQuestion?: isQuestionComplete
  containerType: ContainerType
  isNeutralSetting?: NeutralSetting
}

export const ChoiceInteractionComponent: React.FC<
  ChoiceInteractionProps
> = props => {
  const { currentItemResponse } = useSnapshot(CurrentTestState)
  const { contentPreviewMode } = useSnapshot(ScoringState)
  const {
    children,
    shuffle,
    maxchoices,
    minchoices,
    startLetter,
    responseidentifier: responseDeclarationId,
    accessibilityAttr,
    showAnswer = false,
    onSaveStudentAnswer,
    checkCorrect,
    isCompletedQuestion,
    containerType,
    isNeutralSetting
  } = props

  const [minChoices, maxChoices] = transformChoicesConstraints(
    minchoices,
    maxchoices
  )

  const [focusedChoice, setFocusedChoice] = useState<string>(null)

  const clickOutsideCallback = useCallback(() => setFocusedChoice(null), [])

  useOutsideClick(`[${DATA_CLICK_AWAY_ID}]`, clickOutsideCallback)

  const {
    selected: selectedChoices,
    onSelect,
    responseDeclaration,
    isValid,
    disabled
  } = useSelectableChoice({
    responseIdentifier: responseDeclarationId,
    interactionType: InteractionType.choice,
    constraints: {
      minChoices,
      maxChoices
    },
    onSaveStudentAnswer
  })

  const { correctResponse, groupMode, groupStatistics } =
    useAssessmentItemResponseScoring(
      currentItemResponse?.itemId,
      responseDeclarationId
    )

  const allowMultiple =
    responseDeclaration?.cardinality === Cardinality.multiple ||
    (isNeutralSetting && minChoices > 1)

  // a11y: multi (checkbox/group) unless the item is capped at exactly one — mirrors
  // useSelectableChoice, which forces single only at maxChoices === 1 (0 = unlimited).
  const availableMultipleChoices = allowMultiple && maxChoices !== 1

  const withLetterIndex = !allowMultiple || !!startLetter

  const childrenWithProps = getChildrenWithInjectedProps(
    DomNodes.simpleChoice,
    children,
    {
      minChoices,
      maxChoices,
      responseDeclaration,
      onSelect: (id: string) => onSelect(id),
      selectedChoices: selectedChoices || [],
      availableMultipleChoices,
      isValid,
      disabled
    },
    { withLetterIndex, startLetter }
  )

  const [[prompt], originalSimpleChoice] = useFindNodes(
    childrenWithProps as JSX.Element[],
    [DomNodes.prompt, DomNodes.simpleChoice]
  )

  // Prompt ID is used for aria-labelledby and aria-label
  const promptId = useMemo(() => {
    if (!props.itemId || !responseDeclarationId) return null
    return `${props.itemId}-${responseDeclarationId}-prompt`
  }, [props.itemId, responseDeclarationId])

  // For accessibility, we need to add an id to the prompt
  const promptWithId = useMemo(() => {
    if (!promptId) return prompt
    return React.isValidElement(prompt)
      ? React.cloneElement(prompt, { id: promptId } as React.Attributes)
      : prompt
  }, [prompt, promptId])

  // Shuffle elements once on mount to create a consistent randomized order
  const [shuffledIndices] = useState(() => {
    // Elements ("exercise") should not be shuffled
    if (containerType === "exercise") return null
    return shuffleArray(
      shuffle === "true",
      originalSimpleChoice.map((_, index) => index)
    )
  })

  const simpleChoice = useMemo(() => {
    return shuffledIndices
      ? shuffledIndices.map(index => originalSimpleChoice[index])
      : originalSimpleChoice
  }, [originalSimpleChoice, shuffledIndices])

  const padLength = Math.max(...Object.values(groupStatistics)) === 1 ? 4 : 3

  const feedbackPercentageFormatter = useCallback(
    (percentage: number) =>
      percentageFormatter.format(percentage).padEnd(padLength, " "),
    [padLength]
  )

  const { onQuestionFocus, setBubblesRef, arrowKeysNavigation } =
    useKeyboardNavigation({
      navigationItems: simpleChoice.map(item => item.props.identifier),
      selectedItemIndex: 0,
      direction: "both"
    })

  // Show Answer for Bell Ringer in Ready project
  if (showAnswer) {
    return (
      <>
        {/* [react/jsx-no-useless-fragment] */}
        {containerType === "exercise" ? (
          <Typography
            pl={6}
            variant="h1"
            component="h3"
            color="#558B2F"
            data-automation-id="el-correct-answer"
          >
            {/* Prevent behavior that shows all answers if correct is NOT chosen */}
            {simpleChoice?.length > 1 && !allowMultiple ? (
              "Correct answer not set"
            ) : (
              <>
                {simpleChoice?.length > 1
                  ? "The correct answers are "
                  : "The correct answer is "}
                {pluralizeList(
                  simpleChoice?.map(({ props }) =>
                    String?.fromCharCode(
                      (startLetter || "A").charCodeAt(0) +
                        (Number(props.choiceindex) || 0)
                    )
                  )
                )}
              </>
            )}
          </Typography>
        ) : (
          <>
            <Typography
              pl={6}
              variant="h1"
              component="h3"
              color="#558B2F"
              data-automation-id="el-correct-answer"
            >
              {simpleChoice?.length > 1
                ? "The correct answers are"
                : "The correct answer is"}
            </Typography>
            <CheckboxesWrapper
              role={availableMultipleChoices ? "group" : "radiogroup"}
              {...(promptId
                ? {
                    "aria-labelledby": promptId
                  }
                : { "aria-label": "List of answers" })}
            >
              {simpleChoice.map(({ props, key }) => {
                const {
                  onSelect: _onSelect,
                  disabled: _disabled,
                  ...restProps
                } = props
                return (
                  <SimpleChoiceComponent
                    {...restProps}
                    key={key}
                    letterIndex={String.fromCharCode(
                      (startLetter || "A").charCodeAt(0) +
                        (Number(props.choiceindex) || 0)
                    )}
                    selectedChoices={[props.identifier]}
                  >
                    {props.children}
                  </SimpleChoiceComponent>
                )
              })}
            </CheckboxesWrapper>
          </>
        )}
      </>
    )
  }

  return (
    <>
      {/* [react/jsx-no-useless-fragment] */}
      {currentItemResponse && responseDeclaration && (
        <Wrapper {...accessibilityAttr}>
          {promptWithId}
          {containerType === "assessment" &&
            allowMultiple &&
            maxChoices > 1 && (
              <ConstraintsAlertContainer
                minChoices={minChoices}
                maxChoices={maxChoices}
                selectedCount={selectedChoices?.length}
              />
            )}

          <CheckboxesWrapper
            role={availableMultipleChoices ? "group" : "radiogroup"}
            {...(promptId
              ? {
                  "aria-labelledby": promptId
                }
              : { "aria-label": "List of answers" })}
          >
            {simpleChoice.map(({ props, key }, index) => {
              const { questionCompleted } = isCompletedQuestion?.() ?? {
                questionCompleted: false
              }
              return (
                <SimpleChoiceComponent
                  ref={setBubblesRef(index)}
                  setFocusedChoice={() => {
                    onQuestionFocus(index)
                    setFocusedChoice(props.identifier)
                  }}
                  isFocused={props.identifier === focusedChoice}
                  onChoiceFocus={() => onQuestionFocus(index)}
                  isCorrect={correctResponse?.includes(props.identifier)}
                  groupMode={groupMode}
                  feedbackPercentage={groupStatistics[props.identifier] ?? 0}
                  feedbackFormatter={feedbackPercentageFormatter}
                  checkCorrect={checkCorrect}
                  completed={questionCompleted}
                  key={key}
                  isNeutralSetting={isNeutralSetting}
                  arrowKeysNavigation={arrowKeysNavigation}
                  {...props}
                  letterIndex={
                    withLetterIndex
                      ? String.fromCharCode(
                          (startLetter || "A").charCodeAt(0) + index
                        )
                      : props.letterIndex
                  }
                >
                  {props.children}
                </SimpleChoiceComponent>
              )
            })}
            {groupMode && !contentPreviewMode && (
              <SimpleChoiceComponent
                itemId={props.itemId}
                blankBubble
                groupMode
                feedbackPercentage={groupStatistics["none"]}
                feedbackFormatter={feedbackPercentageFormatter}
              >
                <>No Answer Selected</>
              </SimpleChoiceComponent>
            )}
          </CheckboxesWrapper>
        </Wrapper>
      )}
    </>
  )
}

const Wrapper = styled(Box)(
  () => `
  & hr {
    display: none;
  }
`
)

const CheckboxesWrapper = styled(Stack)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  gap: theme.spacing(1)
}))
